import React from 'react';

/**
 * Reusable Pagination Component
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {number} totalItems - Total number of items
 * @param {number} itemsPerPage - Items per page
 * @param {function} onPageChange - Callback when page changes
 * @param {function} onItemsPerPageChange - Callback when items per page changes (optional)
 * @param {array} itemsPerPageOptions - Options for items per page selector (optional)
 */
export default function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [6, 9, 12, 18, 30],
}) {
  const paginationButtonClass = (disabled) => {
    if (disabled) {
      return 'px-3 py-1 rounded-full border bg-gray-50 text-gray-400 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 cursor-not-allowed';
    }
    return 'px-3 py-1 rounded-full border bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-200 border-gray-200/70 dark:border-gray-600/70 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-300';
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;
    onPageChange(newPage);
  };

  // Generate page numbers with ellipsis - limited to 4 visible page buttons
  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 4;

    if (totalPages <= maxButtons) {
      // If total pages is 4 or less, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 2) {
        // Near start: show 1, 2, 3, ..., last
        pages.push(2);
        pages.push(3);
        pages.push('right-ell');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 1) {
        // Near end: show 1, ..., last-2, last-1, last
        pages.push('left-ell');
        pages.push(totalPages - 2);
        pages.push(totalPages - 1);
        pages.push(totalPages);
      } else {
        // In middle: show 1, ..., current, ..., last
        pages.push('left-ell');
        pages.push(currentPage);
        pages.push('right-ell');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="mt-8 flex flex-col items-center gap-4">
      {/* Top row: Showing info and items per page */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 w-full">
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {startItem}–{endItem} of {totalItems}
          </div>

          {onItemsPerPageChange && (
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="text-sm rounded-md border px-3 py-1 bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-100 border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-300 dark:focus:ring-brand-500"
            >
              {itemsPerPageOptions.map(option => (
                <option key={option} value={option}>{option} / page</option>
              ))}
            </select>
          )}
        </div>

        {/* Mobile: Simple page indicator */}
        <div className="sm:hidden text-sm text-gray-600 dark:text-gray-400">
          Page {currentPage} of {totalPages}
        </div>
      </div>

      {/* Bottom row: Navigation controls - fixed positions */}
      <div className="flex items-center justify-between w-full max-w-2xl">
        {/* Left: Previous button - fixed width */}
        <div style={{ width: '100px' }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={paginationButtonClass(currentPage === 1)}
          >
            Previous
          </button>
        </div>

        {/* Center: Page number buttons or page indicator - fixed width */}
        <div className="flex justify-center" style={{ width: '400px' }}>
          {/* Desktop: Page number buttons */}
          <nav aria-label="Pagination" className="hidden sm:flex items-center gap-1">
            {getPageNumbers().map((p, idx) => {
              if (p === 'left-ell' || p === 'right-ell') {
                return (
                  <span key={p + idx} className="px-2 text-sm text-gray-400 dark:text-gray-500">
                    …
                  </span>
                );
              }

              const isActive = p === currentPage;
              return (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`px-3 py-1 rounded-full text-sm ${
                    isActive
                      ? 'bg-brand-600 text-white shadow'
                      : 'bg-surface dark:bg-gray-870 text-gray-700 dark:text-gray-200 border border-gray-200/70 dark:border-gray-600/70 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-300'
                  }`}
                >
                  {p}
                </button>
              );
            })}
          </nav>

          {/* Mobile: Simple page text indicator */}
          <div className="sm:hidden px-3 py-1 text-sm text-gray-700 dark:text-gray-200">
            {currentPage} / {totalPages}
          </div>
        </div>

        {/* Right: Next button - fixed width */}
        <div style={{ width: '100px' }} className="flex justify-end">
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={paginationButtonClass(currentPage === totalPages)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

